import { lang } from '../localization'
import { ROLE } from '../staticRoleManagement/types'
import { TicketCommentScope } from './ticketWorkflow'

export interface TicketCommentScopeOption {
  value: TicketCommentScope
  label: string
}

const publicScope: TicketCommentScopeOption = {
  value: 'PARTICIPANT_PUBLIC',
  label: 'Visible to ticket participants'
}

const internalScope: TicketCommentScopeOption = {
  value: 'ADOTEL_INTERNAL',
  label: 'Internal Adotel note'
}

/** Client-side affordance only; IAM remains the authority for every scope. */
export function commentScopesForRole(role: ROLE): TicketCommentScopeOption[] {
  return role === ROLE.ADMIN ? [publicScope, internalScope] : [publicScope]
}

/**
 * What the submit button says, which is the last thing read before sending.
 *
 * The button said "Comment" for every scope. That is the one moment the
 * operator is certain to look at — and on the Ctrl+Enter path it is the ONLY
 * scope signal they get, because the shortcut sends without ever visiting the
 * visibility control. Naming the audience in the label puts the consequence at
 * the point of commitment rather than beside the control that set it.
 *
 * This matters more than it looks because the mistake is not reversible: the
 * screen imports no edit or delete mutation, so a note meant for Adotel that
 * lands in front of the reporter cannot be taken back from here.
 *
 * Text, not colour or an icon alone — the label has to survive 1.4.1 and it
 * has to be the accessible name a screen-reader user hears before activating.
 */
export function commentSubmitLabel(scope: TicketCommentScope): string {
  if (scope === 'ADOTEL_INTERNAL') return 'Post internal note'
  if (scope === 'REPORTER_AND_ASSIGNEE_ONLY')
    return 'Reply to reporter and assignee'
  return 'Reply to participants'
}

/**
 * The badge shown above the composer, so the scope is legible while typing.
 *
 * Deliberately duplicates what the radio group already says. The radio sits
 * below the textarea, and an operator writing a long note has it off-screen or
 * out of attention for the whole time they are composing — which is precisely
 * when the belief "this is internal" is formed and never rechecked.
 */
export function commentScopeBadge(scope: TicketCommentScope): {
  label: string
  internal: boolean
} {
  const internal = scope === 'ADOTEL_INTERNAL'
  return {
    internal,
    label: internal ? 'Internal — Adotel staff only' : 'Visible to the reporter'
  }
}

/**
 * Renders a number in the reader's own digit system.
 *
 * Farsi uses Persian digits. A raw JS number substituted into a translated
 * string renders as Latin, so a sentence otherwise entirely in Farsi would
 * carry a Latin numeral — and, worse, would disagree with any numeral a
 * translator wrote directly into another key of the same set.
 */
function localizeCount(count: number): string {
  // 'fa-IR' rather than 'fa': the bare tag resolves to Latin digits in
  // some ICU builds, while the region subtag pins the Persian numbering
  // system that Farsi readers of this panel expect.
  return new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : lang).format(count)
}

/**
 * What the operator is about to do, said in the composer.
 *
 * The visibility Select sits in the left column; the watcher chips sit in the
 * right. So the choice of WHO SEES THIS is made without the list of who that
 * is anywhere near it — and on a narrow viewport the two are never on screen
 * together at all. This puts the consequence next to the control.
 *
 * WHEN UNSURE, OVERSTATE THE AUDIENCE. Every wording decision below breaks
 * that way, because the two errors are not symmetric: an operator who believes
 * more people are reading writes more carefully, while one who believes fewer
 * are reading may write something they would not have. So the public strings
 * name the assignee unconditionally, even though an unassigned ticket has
 * none — naming a reader who is not there costs a little precision, and
 * omitting one who is costs the thing this sentence exists to protect.
 *
 * The count is `watcherUserIds.length` and nothing else, and it is NOT a total
 * headcount: `reporter.userId` is optional (a service-reported ticket has no
 * user behind it), so a sum would silently undercount exactly the tickets
 * whose reporter is hardest to reason about — an understatement, the direction
 * this function refuses. `watcherUserIds` is the array TicketWatchers renders
 * one chip per, so the number can never disagree with the panel it refers to.
 *
 * Returns a key plus variables rather than a string: the caller owns
 * translation, and this stays a pure function that a test can pin without
 * rendering anything.
 */
export function commentRecipientHint(
  scope: TicketCommentScope,
  watcherCount: number
): { key: string; variables?: { count: string } } {
  if (scope === 'ADOTEL_INTERNAL') {
    // "now or later" is load-bearing. Without it the sentence answers "who is
    // reading this today", and the operator's real question is whether this
    // can ever reach the customer. A watcher added tomorrow does not retro-
    // actively gain an internal note, and saying so is what makes the promise
    // usable rather than merely true at the moment it is read.
    return {
      key: 'Only Adotel staff will see this. The reporter will not, now or later.'
    }
  }

  if (scope === 'REPORTER_AND_ASSIGNEE_ONLY') {
    return { key: 'Only the reporter and the assignee will see this.' }
  }

  // Separate keys per count rather than one string with a plural rule:
  // getTranslate does placeholder substitution only, and "1 watchers" in the
  // one place that tells an operator who is about to read their words is not
  // a rough edge worth accepting.
  //
  // Every branch takes the count through {count} — including the singular,
  // where the numeral could have been written into the key. A literal "1" in
  // the key would be translated to a Persian ۱ while a substituted number
  // renders as a Latin 2 or 3, so the same sentence position would change
  // digit system with the count. Formatting is the caller's, via localizeCount.
  if (watcherCount === 0) {
    return {
      key: 'The reporter and the assignee will see this. No one else is watching this ticket yet.'
    }
  }

  if (watcherCount === 1) {
    return {
      key: 'The reporter, the assignee, and {count} watcher will see this.',
      variables: { count: localizeCount(1) }
    }
  }

  return {
    key: 'The reporter, the assignee, and {count} watchers will see this.',
    variables: { count: localizeCount(watcherCount) }
  }
}
